import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BulkCiUploadResult, CiDashboardPagination, CiRecord, CiService } from '../../_services/ci.service';
import { AdvisorService } from '../../_services/advisor/advisor.service';
import { AuthService } from '../../_services/auth.service';

interface IntelRecord {
  id?: string;
  property: string;
  microMarket: string;
  quotedPrice: string;
  benchmarkPSF: string;
  verdict: 'Fairly Priced' | 'Strong Buy' | 'Negotiate' | 'Overpriced';
  lastUpdated: string;
  status: 'Completed' | 'Draft' | 'In Progress';
}

/** `data` of GET /advisor/:id. */
interface AdvisorDetails {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  alternativePhone?: string;
  specialization?: string;
  experience?: string;
  location?: string;
  aboutAdvisor?: string;
  advisorImg?: string;
  role?: { id?: string; label?: string } | string;
  state?: string;
  isApproved?: boolean;
}

interface NavItem {
  label: string;
  icon: string;
  route: string;
  active?: boolean;
  badge?: number;
}

@Component({
  selector: 'app-cip-dashboard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './cip-dashboard.component.html',
  styleUrls: ['./cip-dashboard.component.scss'],
})
export class CipDashboardComponent implements OnInit {
  // ── Stats ────────────────────────────────────────────────
  stats: any;

  // ── Table Data ───────────────────────────────────────────
  recentIntel: IntelRecord[] = [];
  page = 1;
  readonly pageSize = 10;
  pagination: CiDashboardPagination | null = null;
  intelLoading = false;
  // ── CIP Form ─────────────────────────────────────────────
  submitted = false;
  today = new Date().toISOString().split('T')[0];
  showForm = false;

  // ── View Modal ───────────────────────────────────────────
  viewRow: IntelRecord | null = null;
  viewRecord: CiRecord | null = null;
  viewLoading = false;
  viewError = '';
  viewUser: AdvisorDetails | null = null;
  viewUserLoading = false;
  viewUserError = '';

  // ── Bulk Upload Modal ────────────────────────────────────
  bulkOpen = false;
  bulkFile: File | null = null;
  bulkStatus: 'GENERATED' | 'DRAFT' = 'GENERATED';
  bulkUploading = false;
  bulkTemplateLoading = false;
  bulkError = '';
  bulkResult: BulkCiUploadResult | null = null;

  constructor(
    private _CI: CiService,
    private router: Router,
    private _AS: AdvisorService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.getCIData();
  }
  getCIData(page = this.page) {
    this.intelLoading = true;
    this._CI.getDashboardData({ page, limit: this.pageSize }).subscribe({
      next: (res: any) => {
        this.recentIntel = res.recentIntel;
        this.stats = res.stats;
        this.pagination = res.pagination ?? null;
        this.page = res.pagination?.page ?? page;
        this.intelLoading = false;
        // A page emptied by deletions falls back to the last one that has rows.
        const totalPages = res.pagination?.totalPages ?? 0;
        if (!this.recentIntel.length && totalPages && this.page > totalPages) this.getCIData(totalPages);
      },
      error: () => (this.intelLoading = false),
    });
  }

  goToPage(page: number): void {
    const totalPages = this.pagination?.totalPages ?? 1;
    if (page < 1 || page > totalPages || page === this.page || this.intelLoading) return;
    this.getCIData(page);
  }

  /** Page numbers around the current page, with null marking a gap. */
  get pageNumbers(): (number | null)[] {
    const total = this.pagination?.totalPages ?? 0;
    const pages: (number | null)[] = [];
    for (let p = 1; p <= total; p++) {
      if (p === 1 || p === total || Math.abs(p - this.page) <= 1) pages.push(p);
      else if (pages[pages.length - 1] !== null) pages.push(null);
    }
    return pages;
  }

  get pageRangeLabel(): string {
    const p = this.pagination;
    if (!p?.total) return '';
    const from = (p.page - 1) * p.limit + 1;
    return `Showing ${from}–${Math.min(p.page * p.limit, p.total)} of ${p.total}`;
  }

  openNewAnalysis(ciId?: string): void {
    this.router.navigate(['/admin/property/ci-new'], {
      queryParams: ciId ? { ciId } : undefined,
    });
  }

  openView(row: IntelRecord): void {
    this.viewRow = row;
    this.viewRecord = null;
    this.viewError = '';
    this.viewUser = null;
    this.viewUserLoading = false;
    this.viewUserError = '';
    const ciId = row.id ?? (row as any)._id;
    if (!ciId) {
      console.warn('[CI] Dashboard row has no id:', row);
      this.viewUserError = 'This row has no CI id, so the creator cannot be loaded.';
      return;
    }
    this.viewLoading = true;
    this._CI.getCiRecordById(ciId).subscribe({
      next: (record) => {
        if (this.viewRow !== row) return;
        this.viewRecord = record;
        this.viewLoading = false;
        this.loadViewUser(row, record.userId);
      },
      error: (err) => {
        if (this.viewRow !== row) return;
        console.warn('[CI] Could not load CI record', ciId, err);
        this.viewError = err?.message ?? 'Could not load this intelligence.';
        this.viewUserError = 'Could not load the CI record, so the creator is unknown.';
        this.viewLoading = false;
      },
    });
  }

  /** The CI's `userId` is the advisor who created it. */
  private loadViewUser(row: IntelRecord, owner: unknown): void {
    const advisorId = String((owner as any)?._id ?? owner ?? '').trim();
    if (!advisorId) {
      console.warn('[CI] CI record has no userId:', this.viewRecord);
      this.viewUserError = 'This CI was saved without a user id.';
      return;
    }
    this.viewUserLoading = true;
    this._AS.getAdvisor(advisorId).subscribe({
      next: (res: any) => {
        if (this.viewRow !== row) return;
        const advisor: AdvisorDetails | null = res?.data?._id ? this.toAdvisorDetails(res.data) : null;
        this.viewUser = advisor;
        if (!advisor) this.viewUserError = res?.message || 'Advisor API returned no data.';
        this.viewUserLoading = false;
      },
      error: (err) => {
        if (this.viewRow !== row) return;
        console.warn('[CI] Could not load advisor', advisorId, err);
        const status = err?.error?.status;
        this.viewUserError =
          status === 404
            ? `No advisor found for id ${advisorId}. It was probably created by an admin who is not an advisor.`
            : err?.error?.error?.message ?? err?.message ?? 'Could not load advisor.';
        this.viewUserLoading = false;
      },
    });
  }

  joinDefined(...parts: (string | undefined)[]): string {
    return parts.filter(Boolean).join(' · ');
  }

  /** Copies only display fields, so `password` / `c_pass` are never kept in memory. */
  private toAdvisorDetails(data: any): AdvisorDetails {
    const { _id, name, email, phone, alternativePhone, specialization, experience, location, aboutAdvisor, advisorImg, role, state, isApproved } = data;
    return {
      _id, name, email, phone, alternativePhone, specialization, experience, location, aboutAdvisor, advisorImg, state, isApproved,
      role: typeof role === 'string' ? role : { id: role?.id, label: role?.label },
    };
  }

  advisorRole(advisor: AdvisorDetails): string {
    const role = advisor.role;
    return typeof role === 'string' ? role : role?.label ?? role?.id ?? '';
  }

  // ── Bulk Upload ──────────────────────────────────────────

  openBulkUpload(): void {
    this.bulkOpen = true;
    this.resetBulk();
  }

  closeBulkUpload(): void {
    if (this.bulkUploading) return;
    this.bulkOpen = false;
    this.resetBulk();
  }

  private resetBulk(): void {
    this.bulkFile = null;
    this.bulkStatus = 'GENERATED';
    this.bulkError = '';
    this.bulkResult = null;
  }

  onBulkFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.bulkResult = null;
    this.bulkError = '';
    if (file && !/\.xlsx$/i.test(file.name)) {
      this.bulkFile = null;
      this.bulkError = 'Only .xlsx files are supported. Download the template to get the right format.';
      input.value = '';
      return;
    }
    this.bulkFile = file;
  }

  downloadBulkTemplate(): void {
    this.bulkTemplateLoading = true;
    this._CI.downloadBulkTemplate().subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ci-bulk-upload-template.xlsx';
        a.click();
        URL.revokeObjectURL(url);
        this.bulkTemplateLoading = false;
      },
      error: (err) => {
        this.bulkError = err?.message ?? 'Could not download the template.';
        this.bulkTemplateLoading = false;
      },
    });
  }

  submitBulkUpload(): void {
    if (!this.bulkFile || this.bulkUploading) return;
    this.bulkUploading = true;
    this.bulkError = '';
    this.bulkResult = null;
    this._CI
      .bulkUpload(this.bulkFile, { userId: this.auth.getUserId(), status: this.bulkStatus })
      .subscribe({
        next: (result) => {
          this.bulkResult = result;
          this.bulkUploading = false;
          // New CI records appear in the list without a page reload.
          if (result.succeeded > 0) this.getCIData();
        },
        error: (err) => {
          this.bulkError = err?.message ?? 'Bulk upload failed.';
          this.bulkUploading = false;
        },
      });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.bulkOpen) this.closeBulkUpload();
    else this.closeView();
  }

  closeView(): void {
    this.viewRow = null;
    this.viewRecord = null;
    this.viewLoading = false;
    this.viewError = '';
    this.viewUser = null;
    this.viewUserLoading = false;
    this.viewUserError = '';
  }

  editFromView(): void {
    const id = this.viewRow?.id;
    this.closeView();
    this.openNewAnalysis(id);
  }

  formatCurrency(value: number | null): string {
    if (value === null || isNaN(value)) return '—';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  }

  getVerdictClass(verdict: string): string {
    switch (verdict) {
      case 'Fairly Priced':
        return 'verdict-fair';
      case 'Strong Buy':
        return 'verdict-buy';
      case 'Negotiate':
        return 'verdict-negotiate';
      case 'Overpriced':
        return 'verdict-overpriced';
      default:
        return '';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
        return 'status-completed';
      case 'Draft':
        return 'status-draft';
      case 'In Progress':
        return 'status-progress';
      default:
        return '';
    }
  }
}
